'use client';

import { useMemo, useRef, useState } from 'react';
import { PDFDocument } from 'pdf-lib';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type MergePdfFile = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
};

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function createExportFileName(fileCount: number, pageCount: number) {
  return `cleanor.app-merge-pdf-${fileCount}-files-${pageCount}-pages-${formatExportTimestamp(new Date())}.pdf`;
}

function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function isPdfFile(file: File) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

async function readMergePdfFile(file: File): Promise<MergePdfFile> {
  try {
    const bytes = await file.arrayBuffer();
    const pdf = await PDFDocument.load(bytes);

    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: pdf.getPageCount(),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use standard unlocked PDF files.`);
  }
}

export default function BrowserMergePdf() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [files, setFiles] = useState<MergePdfFile[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [resultPageCount, setResultPageCount] = useState<number | null>(null);

  const totalSourceSize = useMemo(() => files.reduce((sum, item) => sum + item.size, 0), [files]);
  const totalPages = useMemo(() => files.reduce((sum, item) => sum + item.pageCount, 0), [files]);

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
    setResultPageCount(null);
  }

  async function addFiles(fileList: FileList | File[] | null) {
    if (!fileList?.length) {
      return;
    }

    setErrorMessage(null);
    setCreditMessage(null);

    const pdfFiles = Array.from(fileList).filter(isPdfFile);

    if (!pdfFiles.length) {
      setErrorMessage('Choose PDF files to merge.');
      return;
    }

    try {
      const nextFiles = await Promise.all(pdfFiles.map((file) => readMergePdfFile(file)));
      setFiles((current) => [...current, ...nextFiles]);
      resetExportState();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'One or more PDFs could not be read.',
      );
    }
  }

  function moveFile(fileId: string, direction: -1 | 1) {
    setFiles((current) => {
      const index = current.findIndex((item) => item.id === fileId);

      if (index < 0) {
        return current;
      }

      const nextIndex = index + direction;

      if (nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }

      const nextFiles = [...current];
      const [item] = nextFiles.splice(index, 1);
      nextFiles.splice(nextIndex, 0, item);
      return nextFiles;
    });
    resetExportState();
  }

  function removeFile(fileId: string) {
    setFiles((current) => current.filter((item) => item.id !== fileId));
    resetExportState();
  }

  function clearFiles() {
    setFiles([]);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function mergePdfFiles() {
    if (files.length < 2) {
      setErrorMessage('Add at least two PDF files first.');
      return;
    }

    setIsMerging(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'merge-pdf' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another PDF merge right now.';
        throw new Error(message);
      }

      const mergedPdf = await PDFDocument.create();

      for (const item of files) {
        const bytes = await item.file.arrayBuffer();
        const sourcePdf = await PDFDocument.load(bytes);
        const pageIndexes = sourcePdf.getPageIndices();
        const copiedPages = await mergedPdf.copyPages(sourcePdf, pageIndexes);

        copiedPages.forEach((page) => {
          mergedPdf.addPage(page);
        });
      }

      const mergedBytes = Uint8Array.from(await mergedPdf.save());
      const blob = new Blob([mergedBytes.buffer], { type: 'application/pdf' });
      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextDownloadName = createExportFileName(files.length, totalPages);

      resetExportState();
      downloadUrlRef.current = nextDownloadUrl;
      setResultSize(blob.size);
      setResultPageCount(totalPages);
      triggerBrowserDownload(nextDownloadUrl, nextDownloadName);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Merged PDF download started. Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Merged PDF download started. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Merged PDF download started.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The PDF files could not be merged.',
      );
    } finally {
      setIsMerging(false);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Merge multiple PDFs in the browser</h2>
          <p>
            Upload PDF files, set the file order, and export one merged PDF locally without sending
            the documents to a server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void addFiles(event.dataTransfer.files);
            }}
            type="button"
          >
            <div>
              <strong>
                {files.length
                  ? `${files.length} PDF file${files.length === 1 ? '' : 's'} ready to merge`
                  : 'Choose PDF files or drop them here'}
              </strong>
              <span>
                {files.length
                  ? `${totalPages} total pages • ${formatFileSize(totalSourceSize)} source size • merge in file order`
                  : 'Works best with standard unlocked PDF files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            multiple
            onChange={(event) => void addFiles(event.target.files)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={files.length < 2 || isMerging}
              onClick={() => void mergePdfFiles()}
              type="button"
            >
              {isMerging ? 'Merging PDFs…' : 'Merge PDF'}
            </button>
            {files.length ? (
              <button className="button button-ghost" onClick={clearFiles} type="button">
                Clear files
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Client-side PDF merger</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → PDF</span>
            <span>File order</span>
            <span>One output</span>
            <span>Local export</span>
          </div>
          <p>
            Keep the files in the right order first, then export one merged document with a single
            click.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Use standard unlocked PDFs. Password-protected files are not supported here.
            </span>
          </div>
          {files.length ? (
            <div className="tool-live-stats">
              <div>
                <strong>Files</strong>
                <span>{files.length}</span>
              </div>
              <div>
                <strong>Total pages</strong>
                <span>{totalPages}</span>
              </div>
              <div>
                <strong>Source total</strong>
                <span>{formatFileSize(totalSourceSize)}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Merged PDF size</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              {resultPageCount ? (
                <div>
                  <strong>Last export</strong>
                  <span>{`${resultPageCount} page${resultPageCount === 1 ? '' : 's'}`}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {files.length ? (
        <div className="tool-live-preview tool-pdf-preview">
          <h3>Files</h3>
          <div className="tool-pdf-page-list">
            {files.map((item, index) => (
              <article className="tool-pdf-page-card" key={item.id}>
                <div className="tool-pdf-page-thumb tool-pdf-file-thumb">
                  <strong>{item.pageCount}</strong>
                  <span>{item.pageCount === 1 ? 'page' : 'pages'}</span>
                </div>
                <div className="tool-pdf-page-copy">
                  <strong>{`${index + 1}. ${item.name}`}</strong>
                  <span>{`${item.pageCount} page${item.pageCount === 1 ? '' : 's'} • ${formatFileSize(item.size)}`}</span>
                  <span>Files merge from top to bottom in this order.</span>
                </div>
                <div className="tool-pdf-page-actions">
                  <button
                    className="tool-pdf-page-action"
                    disabled={index === 0}
                    onClick={() => moveFile(item.id, -1)}
                    type="button"
                  >
                    Up
                  </button>
                  <button
                    className="tool-pdf-page-action"
                    disabled={index === files.length - 1}
                    onClick={() => moveFile(item.id, 1)}
                    type="button"
                  >
                    Down
                  </button>
                  <button
                    className="tool-pdf-page-action tool-pdf-page-action-danger"
                    onClick={() => removeFile(item.id)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}
